// src/components/shared/constants/taxonomyMirror.js
// Centralized taxonomy definition for visual categories and vibes.
// This mirrors the backend registry structure (v6.0.0).
// Used by: NewProject.js, ProjectOverview.js, VisualGenerationPanel.js

export const SMART_AUTO_VIBE = { id: "smart_auto", label: "Smart Auto", desc: "AI picks the best style for your content. Adapts to sentiment." };

export const TAXONOMY_MIRROR = [
    {
        id: "cinematic",
        label: "Cinematic",
        vibes: [
            { id: "moody_drama", label: "Moody Drama", desc: "Dark, intimate, tension. Warm tungsten pools in deep shadow." },
            { id: "golden_hour", label: "Golden Hour", desc: "Rich golden sunlight, warm haze, epic aspirational atmosphere." },
            { id: "film_noir_classic", label: "Film Noir Classic", desc: "1940s monochrome. Hard shadows, venetian blinds, smoke." },
            { id: "cyberpunk_neon", label: "Cyberpunk Neon", desc: "Neon pink, cyan, purple. Wet streets, fog, futuristic." },
        ]
    },
    {
        id: "animated",
        label: "Animated & Illustrated",
        vibes: [
            { id: "3d_rounded_toy", label: "3D Rounded Toy", desc: "Smooth plastic, big eyes, hyper-saturated. Fun and bouncy." },
            { id: "3d_geometric", label: "3D Geometric Low-Poly", desc: "Faceted polygon surfaces, flat shading, papercraft feel." },
            { id: "watercolor", label: "Watercolor", desc: "Soft flowing pigments on textured paper. Organic and handmade." },
            { id: "claymation", label: "Claymation", desc: "Plasticine characters with fingerprints. Cotton wool clouds." },
            { id: "stop_motion", label: "Stop Motion", desc: "Felt, balsa wood, pipe cleaners. Handmade miniature sets." },
            { id: "hand_drawn", label: "Hand-Drawn", desc: "Graphite pencil on paper. Cross-hatching, line weight variation." },
            { id: "comic_book", label: "Comic Book", desc: "Bold ink outlines, flat fills, halftone dots, speed lines." },
            { id: "flat_illustration", label: "Flat Illustration", desc: "Clean geometric shapes, flat color fills, no gradients." },
            { id: "illustrated_storybook", label: "Illustrated Storybook", desc: "Rich gouache painterly, jewel-tone colors, cozy warmth." },
{ id: "ghibli_anime", label: "Ghibli Anime", desc: "Hand-painted anime. Warm, dreamy, lush nature, Miyazaki-inspired." },
            { id: "anime", label: "Anime", desc: "Japanese anime style. Dynamic angles, expressive eyes, vivid color." },
            { id: "pixel_art", label: "Pixel Art", desc: "16-bit retro game style. Sharp pixels, dithered shading." },
            { id: "ink_style", label: "Ink Style", desc: "Traditional ink wash painting. Sumi-e, flowing brushstrokes." },
        ]
    },
    {
        id: "vintage",
        label: "Vintage & Retro",
        vibes: [
            { id: "vhs_retro", label: "VHS Retro", desc: "1990s camcorder. Tracking lines, washed-out warm, chromatic aberration." },
            { id: "super_8_film", label: "Super 8 Film", desc: "1970s home movie. Heavy grain, sepia, dust and scratches." },
            { id: "faded_polaroid", label: "Faded Polaroid", desc: "Muted pastels, cyan shift, overexposure, light leaks." },
            { id: "kodachrome", label: "Kodachrome", desc: "Rich oversaturated warm slide film. Punchy reds, vivid greens." },
        ]
    },
    {
        id: "fantasy",
        label: "Fantasy & Surreal",
        vibes: [
            { id: "dreamlike", label: "Dreamlike", desc: "Real subject, impossible world. Soft ethereal, floating quality." },
            { id: "surreal", label: "Surreal", desc: "Impossible physics rendered photographically. Dream logic." },
            { id: "fantasy", label: "Fantasy", desc: "Bioluminescent magic, enchanted sparkles, fairy-tale atmosphere." },
            { id: "dark_fantasy", label: "Dark Fantasy", desc: "Gothic, moonlit, candlelit. Ancient stone, deep darkness." },
        ]
    },
    {
        id: "commercial",
        label: "Commercial & Editorial",
        vibes: [
            { id: "clean_bright", label: "Clean & Bright", desc: "Crisp, punchy, Instagram-polished. Even lighting, clean whites." },
            { id: "warm_editorial", label: "Warm Editorial", desc: "Magazine lifestyle. Portra tones, soft diffused daylight." },
            { id: "minimalist", label: "Minimalist", desc: "Single subject, vast negative space. Gallery aesthetic." },
            { id: "fashion_editorial", label: "Fashion Editorial", desc: "Dramatic sculpted studio lighting. Restrained, high-end." },
            { id: "portrait", label: "Portrait", desc: "Studio-quality people photography. Natural skin tones." },
            { id: "ultra_real", label: "Ultra Real", desc: "Commercial DSLR quality. Razor-sharp detail and material fidelity." },
            { id: "close_up", label: "Close-Up Shot", desc: "Extreme macro detail. Subject fills entire frame edge to edge." },
        ]
    },
    {
        id: "authentic",
        label: "Authentic & UGC",
        vibes: [
            { id: "raw_documentary", label: "Raw Documentary", desc: "Handheld, mixed light, unpolished. Behind-the-scenes feel." },
            { id: "nature_documentary", label: "Nature Documentary", desc: "Telephoto compression, patient, observational. BBC Earth." },
            { id: "amateur_mobile", label: "Amateur / Mobile", desc: "Smartphone footage. Casual framing, portrait mode blur." },
            { id: "selfie_influencer", label: "Selfie / Influencer", desc: "Front camera, arm's length, direct eye contact. Vlogging." },
            { id: "viral", label: "Viral", desc: "Scroll-stopping. Ultra-wide, high contrast, visual impact." },
        ]
    }
];

// Helper to get the default category
export const getDefaultCategory = () => TAXONOMY_MIRROR[0];

// Helper to get the default vibe for a category
export const getDefaultVibe = (categoryId) => {
    const category = TAXONOMY_MIRROR.find(c => c.id === categoryId);
    return category?.vibes[0] || TAXONOMY_MIRROR[0].vibes[0];
};

// Helper to find vibes for a category
export const getVibesForCategory = (categoryId) => {
    const category = TAXONOMY_MIRROR.find(c => c.id === categoryId);
    return category?.vibes || [];
};
